import { useMemo } from 'react';
import { CreditCard, DollarSign, Calendar, Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/data-display/StatCard';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { ROUTES } from '@/constants';
import { useOrigination } from '@/features/origination/hooks';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Credito', path: ROUTES.CREDIT_DASHBOARD },
  { label: 'Originacion', path: ROUTES.ORIGINATION },
];

const fmt = (v: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v);

export function OriginationPage() {
  const { weeks, isLoading, isError, refetch } = useOrigination();
  const items = weeks ?? [];
  const latest = items[items.length - 1];
  const maxCredits = useMemo(() => Math.max(...(items.map((w) => w.credits_originated)), 1), [items]);

  if (isLoading) return (<div><PageHeader title="Originacion de Creditos" breadcrumbs={BREADCRUMBS} /><div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={`s-${i.toString()}`} variant="rectangular" height={80} />)}</div></div>);
  if (isError) return (<div><PageHeader title="Originacion de Creditos" breadcrumbs={BREADCRUMBS} /><div className="flex flex-col items-center rounded-lg border border-border bg-card px-6 py-16"><AlertTriangle size={48} className="mb-4 text-muted-foreground" /><h3 className="text-lg font-semibold">Error al cargar originacion</h3><Button className="mt-4" onClick={() => refetch()}><RefreshCw size={16} /> Reintentar</Button></div></div>);

  return (
    <div>
      <PageHeader title="Originacion de Creditos" subtitle="Creditos originados por semana" breadcrumbs={BREADCRUMBS} actions={<div className="flex items-center gap-2"><HelpButton pageKey="origination" /><Button onClick={() => refetch()}><RefreshCw size={16} /> Actualizar</Button></div>} />
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Creditos Semana" value={latest?.credits_originated ?? 0} icon={CreditCard} />
          <StatCard title="Monto Semana" value={fmt(latest?.total_amount_mxn ?? 0)} icon={DollarSign} />
          <StatCard title="Monto Promedio" value={fmt(latest?.avg_amount_mxn ?? 0)} icon={Calendar} />
          <StatCard title="Plazo Promedio" value={`${latest?.avg_term_weeks ?? 0} semanas`} icon={Clock} />
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h3 className="mb-4 text-lg font-semibold">Creditos por Semana</h3>
          <div className="flex items-end gap-2" style={{ height: 200 }}>
            {items.map((w) => (
              <div key={w.week} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-xs tabular-nums font-medium">{w.credits_originated}</span>
                <div className="w-full rounded-t bg-blue-500 transition-all" style={{ height: `${(w.credits_originated / maxCredits) * 160}px` }} />
                <span className="text-xs text-muted-foreground">{w.week}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-6 py-4"><h3 className="text-lg font-semibold">Detalle Semanal</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-muted-foreground"><th className="px-6 py-3 font-medium">Semana</th><th className="px-6 py-3 text-right font-medium">Creditos</th><th className="px-6 py-3 text-right font-medium">Monto Total</th><th className="px-6 py-3 text-right font-medium">Monto Promedio</th><th className="px-6 py-3 text-right font-medium">Plazo Prom.</th></tr></thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.week} className="border-b border-border/50">
                    <td className="px-6 py-3 font-medium">{w.week}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{w.credits_originated}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{fmt(w.total_amount_mxn)}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{fmt(w.avg_amount_mxn)}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{w.avg_term_weeks} sem</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
